/**
 * The order plan's questions (XC-INV2PO answering note §4): `GET /api/v1/inv2po/questions` (gateway →
 * agent_service). One card per open question in his words (which of his products a customer's item
 * is, whether an order was already made at the count, whether finished stock is free, whether the rest
 * of an order shipped, whether two copies are one order, whether a document is an order at all,
 * which of his orders his own invoice or packing slip is for (note XC-INV2PO_U4_Own_Papers_And_Email_Orders §5),
 * whether to order his ask-at-zero material now: pallets, which of his counted items a vendor's item on an order he
 * placed is: order counting's row question; what goes in a case of a held recipe), his answers of the last week with Undo, and
 * whether this person may answer (owner/admin; everyone signed in sees the cards).
 *
 * Ids are for keys and calls only, never printed. Objects are not strict: a field the engine adds
 * later must not blank the Inbox.
 */

import { z } from 'zod';

export const OrderQuestionChoiceSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  hint: z.string().nullable().optional(),
  held: z.boolean().optional(),
  /** AW-144: "product" for one of his accounting system's products (a recipe choice has none). */
  kind: z.string().optional(),
  /** AW-144: "customer" for a product invoiced to this order's customer, "other" for his other products. */
  group: z.string().optional(),
  /** AW-144: his own row says what it holds (shown as his). */
  confirmed: z.boolean().optional(),
  /** His own row says it holds nothing AskWingo counts: a countable hold's picker leaves it off. */
  holds_nothing: z.boolean().optional(),
  /** AW-144: the filled-in row a pick confirms in "Your products" before it answers, with the line he reads first. */
  confirm: z
    .object({
      prompt: z.string(),
      holds: z.string().nullable().optional(),
      item: z.string().nullable().optional(),
      amount: z.union([z.string(), z.number()]).nullable().optional(),
    })
    .passthrough()
    .nullable()
    .optional(),
});

export const OrderQuestionLineSchema = z.object({
  line_no: z.number().int().positive(),
  label: z.string(),
  on_hand: z.string().nullable().optional(),
  product: z.string().nullable().optional(),
  /** AW-144 slice 3, "count this email order": what one of his product holds, and the count the server could
   * fill in by value ("200 pounds of seasoning = 4 x ..."), or none for him to give. */
  holds: z.string().nullable().optional(),
  count: z.string().nullable().optional(),
  count_words: z.string().nullable().optional(),
  /** His own invoice to that customer billing this product, shown beside the count, never filed (call 22). */
  invoice: z.string().nullable().optional(),
});

/** One material of a held recipe's case and its amount per case (held-recipes note §6 R2), as the server words it. */
export const RecipePartSchema = z.object({
  material_key: z.string().min(1),
  material_label: z.string().nullable().optional(),
  quantity: z.number(),
});

/** A held recipe's card data: what his workbook reads, the one-tap proposal (a sibling matching exactly: its shared
 * stock carried, its own materials for him to pick), his similar products, and his count's materials to choose from. */
export const RecipeBlockSchema = z.object({
  product_key: z.string().nullable().optional(),
  reads: z.array(RecipePartSchema).default([]),
  proposal: z
    .object({
      sibling: z.string(),
      sibling_label: z.string().nullable().optional(),
      carried: z.array(RecipePartSchema).default([]),
      his_own: z.array(RecipePartSchema).default([]),
    })
    .nullable()
    .optional(),
  siblings: z
    .array(z.object({ product_key: z.string(), label: z.string().nullable().optional(), components: z.array(RecipePartSchema).default([]) }))
    .default([]),
  materials: z.array(z.object({ key: z.string().min(1), label: z.string().min(1) })).default([]),
});

/** One material of his answer to a held recipe, sent with "recipe" (the server checks it again by value). */
export interface RecipeComponent {
  material_key: string;
  quantity: string;
}

/** His count of one line's product, sent with "count" (AW-144 slice 3). */
export interface LineCount {
  line_no: number;
  count: string;
}

/** One row of his count of an order held on its lines, sent with "count_it" (held-order count note §3 R3): one of
 * his products and how many (the server checks each again by value). */
export interface HeldCountRow {
  product_key: string;
  count: string;
}

export const ORDER_QUESTION_KINDS = [
  'which_product',
  'made_already',
  'free_stock',
  'shipped_part',
  'same_order',
  'is_order',
  'which_order',
  'ask_at_zero',
  'count_order',
  'which_row',
  'recipe',
] as const;

/** "Ask me again in N weeks" on his ask-at-zero (pallets) card: N whole, within the server's bounds. */
export const AskLaterSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  min_weeks: z.number().int().positive(),
  max_weeks: z.number().int().positive(),
});

export const OrderQuestionCardSchema = z.object({
  id: z.string().uuid(),
  kind: z.string().min(1),
  asked_at: z.string().datetime({ offset: true }).nullable(),
  order_label: z.string().nullable().optional(),
  item_key: z.string().nullable().optional(),
  title: z.string().min(1),
  detail: z.array(z.string()).default([]),
  choices: z.array(OrderQuestionChoiceSchema).default([]),
  more: z.array(OrderQuestionChoiceSchema).default([]),
  lines: z.array(OrderQuestionLineSchema).default([]),
  later: AskLaterSchema.nullable().optional(),
  /** AW-144: the order's customer, as printed, over the products invoiced to them. */
  customer_name: z.string().nullable().optional(),
  /** Held recipes: what goes in a case (only on a `recipe` card). */
  recipe: RecipeBlockSchema.nullable().optional(),
  /** An order held on its lines (held-order count note §3 R2): its card offers "Count it in my plan" with his
   * products to pick from (the route sends the customer's; the page adds his recipe and other products, sent once
   * beside the cards) and at most `max_rows`. */
  countable: z.boolean().optional(),
  products: z.array(OrderQuestionChoiceSchema).optional(),
  max_rows: z.number().int().positive().optional(),
});

export const AnsweredOrderQuestionSchema = z.object({
  answer_id: z.string().uuid(),
  question_id: z.string().uuid(),
  kind: z.string(),
  words: z.string(),
  answered_at: z.string().datetime({ offset: true }),
});

/**
 * AW-144: the route sends his other products once, beside the cards (`other_products`, by name), never on each card
 * (/ship security r2: 100 cards x 2,000 products). Each "Which product?" card offers, after its own choices, every one
 * of them it does not already offer.
 */
function withOtherProducts<C extends OrderQuestionCard>(
  card: C,
  others: OrderQuestionChoice[],
  recipes: OrderQuestionChoice[],
): C {
  if (card.kind === 'is_order' && card.countable) return withHisProducts(card, others, recipes);
  if (card.kind !== 'which_product' || others.length === 0) return card;
  const own = new Set([...card.choices, ...card.more].map((c) => c.value));
  return { ...card, more: [...card.more, ...others.filter((o) => !own.has(o.value))] };
}

/**
 * Held-order count: a countable hold's picker offers the products the route put on the card (invoiced to this
 * customer), then his recipe products, then his other products, each once; a product his row says holds nothing is
 * left off (the server refuses it). Both lists are sent once beside the cards (/ship s1437 security H1).
 */
function withHisProducts<C extends OrderQuestionCard>(
  card: C,
  others: OrderQuestionChoice[],
  recipes: OrderQuestionChoice[],
): C {
  const own = card.products ?? [];
  const seen = new Set(own.map((c) => c.value));
  const more = [...recipes.map((r) => ({ ...r, group: 'recipe' })), ...others.map((o) => ({ ...o, group: 'other' }))].filter(
    (c) => !c.holds_nothing && !seen.has(c.value) && (seen.add(c.value), true),
  );
  return { ...card, products: [...own, ...more] };
}

export const OrderQuestionsSchema = z
  .object({
    cards: z.array(OrderQuestionCardSchema),
    total: z.number().int().nonnegative(),
    answered: z.array(AnsweredOrderQuestionSchema).default([]),
    can_answer: z.boolean(),
    answer_reason: z.string().nullable().optional(),
    other_products: z.array(OrderQuestionChoiceSchema).default([]),
    /** Held-order count: his recipe products, once, while a countable card is shown. */
    recipe_products: z.array(OrderQuestionChoiceSchema).default([]),
    /** Left out for this role (note XC-INV2PO role view v0.2 §6.2): open cards, and his answers, never in `total`. */
    hidden_for_role: z.number().int().nonnegative().default(0),
    answered_hidden_for_role: z.number().int().nonnegative().default(0),
  })
  .transform(({ other_products, recipe_products, ...rest }) => ({
    ...rest,
    cards: rest.cards.map((c) => withOtherProducts(c, other_products, recipe_products)),
  }));

export const ReplanSchema = z.object({
  status: z.string(),
  outcome: z.string().nullable().optional(),
  message: z.string(),
});

export const AnswerResultSchema = z.object({
  answer_id: z.string().uuid(),
  replan: ReplanSchema,
});

export const UndoResultSchema = z.object({
  undo_id: z.string().uuid(),
  replan: ReplanSchema,
});

export type OrderQuestionChoice = z.infer<typeof OrderQuestionChoiceSchema>;
export type AskLater = z.infer<typeof AskLaterSchema>;
export type OrderQuestionLine = z.infer<typeof OrderQuestionLineSchema>;
export type RecipePart = z.infer<typeof RecipePartSchema>;
export type RecipeBlock = z.infer<typeof RecipeBlockSchema>;
export type OrderQuestionCard = z.infer<typeof OrderQuestionCardSchema>;
export type AnsweredOrderQuestion = z.infer<typeof AnsweredOrderQuestionSchema>;
export type OrderQuestions = z.infer<typeof OrderQuestionsSchema>;
export type Replan = z.infer<typeof ReplanSchema>;
export type AnswerResult = z.infer<typeof AnswerResultSchema>;
export type UndoResult = z.infer<typeof UndoResultSchema>;
